export interface BetaTestQuestion {
  id: string;
  question: string;
  inputType: 'text' | 'dropdown';
  placeholder?: string;
  dropdownOptions?: string[];
}

export interface BetaTestComponent {
  id: string;
  name: string;
  questions: BetaTestQuestion[];
}

export interface BetaTestPage {
  id: string;
  name: string;
  components: BetaTestComponent[];
}

export const betaTestData: BetaTestPage[] = [
  {
    id: 'jobseeker_profile',
    name: 'Jobseeker Profile',
    components: [
      {
        id: 'edit_profile_modal',
        name: 'Edit Profile modal',
        questions: [
          {
            id: 'edit_profile_q1',
            question: 'Was there anything you expected to be editable but couldn\'t find?',
            inputType: 'dropdown',
            placeholder: 'I expected to be able to edit...',
            dropdownOptions: [
              'I expected to be able to edit my name, title, and bio, and all of those were there.',
              'I expected to be able to edit my public profile URL / other'
            ]
          }
        ]
      },
      {
        id: 'image_crop_modal',
        name: 'Image crop modal',
        questions: [
          {
            id: 'image_crop_q1',
            question: 'Was anything about the cropping experience confusing or harder than it should be?',
            inputType: 'dropdown',
            placeholder: 'I struggled with ___ because ___',
            dropdownOptions: [
              'The cropping tool was simple and worked exactly as I expected',
              'I struggled with zooming and positioning the crop area because the slider was too sensitive'
            ]
          }
        ]
      },
      {
        id: 'developer_tier_modal',
        name: 'Developer Tier Modal',
        questions: [
          {
            id: 'dev_tier_q1',
            question: 'Do you think the number of challenges required to unlock each tier feels fair?',
            inputType: 'dropdown',
            placeholder: 'I think ___ tier should require ___ challenges instead of ___ because ___',
            dropdownOptions: [
              'I think the current system should require the current number of challenges instead of changing it, because the progression feels fair and rewards dedication.',
              'Requiring so many challenges encourages quantity over quality'
            ]
          }
        ]
      },
      {
        id: 'public_link_toggle',
        name: 'Public link toggle',
        questions: [
          {
            id: 'public_link_q1',
            question: 'Was it clear what the public link toggle actually does?',
            inputType: 'dropdown',
            placeholder: 'I thought turning it on would ___, but instead it ___',
            dropdownOptions: [
              'I thought turning it on would make my profile public at a shareable link, and it did exactly that, which was clear.',
              'The purpose of the public link toggle was unclear'
            ]
          },
          {
            id: 'public_link_q2',
            question: 'Would you feel confident sharing this link with someone professionally? Why or why not?',
            inputType: 'dropdown',
            placeholder: 'I\'d be comfortable sharing it if ___, but I hesitate because ___',
            dropdownOptions: [
              'I\'d be comfortable sharing it if it looks just like this.',
              'I hesitate because I feel like certain section/s of my profile aren\'t impressive enough yet.'
            ]
          }
        ]
      },
      {
        id: 'add_edit_skill_modal',
        name: 'Add/edit skill modal',
        questions: [
          {
            id: 'skill_modal_q1',
            question: 'Did you feel confident about how to describe your skill? Or did it feel too vague, too rigid, or unnecessary?',
            inputType: 'dropdown',
            placeholder: 'Writing the description felt ___ because ___',
            dropdownOptions: [
              'Writing the description felt empowering because I could add specific context about how I\'ve used the skill.',
              'Writing the description felt unnecessary because I wasn\'t sure what to write, and the projects should speak for themselves.'
            ]
          }
        ]
      },
      {
        id: 'add_edit_project_modal',
        name: 'Add/edit project modal',
        questions: [
          {
            id: 'project_modal_q1',
            question: 'Did you feel like you had enough freedom to describe your project in a way that feels true to what you built?',
            inputType: 'dropdown',
            placeholder: 'I wanted to share ___ but couldn\'t find a place for it because ___',
            dropdownOptions: [
              'I wanted to share the business problem I was solving but couldn\'t find a place for it because the fields are all focused on technical details',
              'I wouldn\'t change anything about the add project modal.'
            ]
          }
        ]
      },
      {
        id: 'project_showcase',
        name: 'Project showcase',
        questions: [
          {
            id: 'project_showcase_q1',
            question: 'What part of this presentation format makes you most confident about being judged fairly? What makes you hesitate?',
            inputType: 'dropdown',
            placeholder: 'I feel confident when ___, but I worry about ___',
            dropdownOptions: [
              'I feel confident when viewers watch my video demo',
              'I worry about my design choices being judged without the full context.'
            ]
          }
        ]
      },
      {
        id: 'system_design_interview',
        name: 'Took system design interview',
        questions: [
          {
            id: 'sdi_q1',
            question: 'Were the instructions clear enough to know what was expected—and how to succeed?',
            inputType: 'dropdown',
            placeholder: 'The instructions made it clear I should ___, but I wasn\'t sure about ___',
            dropdownOptions: [
              'The instructions made it clear I should describe a high-level architecture',
              'wasn\'t sure about whether I should be drawing on the whiteboard or just speaking.'
            ]
          },
          {
            id: 'sdi_q2',
            question: 'Did being graded by an AI feel objective, or did you wish a human were involved?',
            inputType: 'dropdown',
            placeholder: 'I liked that it was AI-graded because ___, but I\'d prefer a human for ___',
            dropdownOptions: [
              'I liked that it was AI-graded because it felt objective and unbiased',
              'I\'d prefer a human for understanding the subtle trade-offs in my design.'
            ]
          },
          {
            id: 'sdi_q3',
            question: 'Do you think this is a fair way to present technical thinking to employers—compared to a live interview?',
            inputType: 'dropdown',
            placeholder: 'This shows ___ better than a live interview, but misses ___',
            dropdownOptions: [
              'This shows my structured thinking process better than a live interview',
              'This process misses how I communicate and react to questions under pressure'
            ]
          },
          {
            id: 'sdi_q4',
            question: 'Did the design of the interview help you focus—or get in the way?',
            inputType: 'dropdown',
            placeholder: 'The interface helped me ___, but made it harder to ___',
            dropdownOptions: [
              'The interface helped me stay calm due to its minimal design,',
              'The interfacemade it harder to see the original problem description and the whiteboard at the same time.'
            ]
          }
        ]
      },
      {
        id: 'sdi_display',
        name: 'System design interview display on profile',
        questions: [
          {
            id: 'sdi_display_q1',
            question: 'Did the AI-generated report (strengths, improvements, score breakdown) feel like personalized, expert feedback or a generic template?',
            inputType: 'dropdown',
            placeholder: 'The feedback on ___ felt spot-on, but the suggestion about ___ seemed generic because ___.',
            dropdownOptions: [
              'The feedback felt spot-on',
              'The feedback was irrelevant'
            ]
          },
          {
            id: 'sdi_display_q2',
            question: 'Did the final score feel fair, too lenient, or too strict? If it felt off, which question do you think was graded most unfairly?',
            inputType: 'dropdown',
            placeholder: 'My score felt ___ (fair/strict/lenient). I think my answer for Q___ about ___ was graded unfairly because ___.',
            dropdownOptions: [
              'My score felt fair. I think my answer for all questions about the overall system was graded appropriately.',
              'My score felt too strict.'
            ]
          },
          {
            id: 'sdi_display_q3',
            question: 'We give you the options to display your result, hide it, or pay for another attempt. Does this model feel fair and give you control, or does the \'pay to retake\' feel like a penalty?',
            inputType: 'dropdown',
            placeholder: 'Giving me these options felt ___ (fair/empowering/limiting). The pay-to-retake part feels like ___ because ___.',
            dropdownOptions: [
              'Giving me these options felt fair. The pay-to-retake part feels like a reasonable business choice because I understand that running the AI evaluation costs money.',
              'Giving me these options felt limiting. The pay-to-retake part feels like a penalty for having a bad day because my skill shouldn\'t be hidden behind a paywall.'
            ]
          }
        ]
      },
      {
        id: 'completed_challenges',
        name: 'Completed challenges display',
        questions: [
          {
            id: 'challenges_display_q1',
            question: 'We break your skills down into three levels: Metaskills (the broad category), Subskills (the specific competency), and the actual Challenge you completed. Is this three-level hierarchy intuitive, or is it overly complex?',
            inputType: 'dropdown',
            placeholder: 'The breakdown felt ___.',
            dropdownOptions: [
              'The breakdown felt intuitive.',
              'The breakdown felt a little too granular.'
            ]
          },
          {
            id: 'challenges_display_q2',
            question: 'Does this detailed breakdown feel like a powerful signal of your expertise to an employer, or is there too much information? What is the single most important piece of data in this entire view, and what\'s the least important (or most noisy)?',
            inputType: 'dropdown',
            placeholder: 'This view feels ___ for showing my skills. The most important piece of info is ___. The least important is ___ because ___.',
            dropdownOptions: [
              'This view feels like a powerful summary for showing my skills.',
              'This view feels a bit noisy for showing my skills.'
            ]
          }
        ]
      },
      {
        id: 'challenge_showcase',
        name: 'Challenge showcase',
        questions: [
          {
            id: 'challenge_showcase_q1',
            question: 'Is there any information missing from this showcase (like a text box for your own summary)?',
            inputType: 'dropdown',
            placeholder: 'Employers would probably ask me why I ___. I wish there was a place to add ___ to explain that.',
            dropdownOptions: [
              'I wish there was a place to add a short \'Design Decisions\' section',
              'I wish there was a place to add comments on the video timeline'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'jobseeker_messages',
    name: 'Jobseeker Messages Page',
    components: [
      {
        id: 'messaging_component',
        name: 'Messaging component',
        questions: [
          {
            id: 'messaging_q1',
            question: 'Did the flow of sending a message—typing, attaching files, replying—feel fluid and fast, or did you notice any lag or friction that made it feel cumbersome?',
            inputType: 'dropdown',
            placeholder: 'The experience was generally ___. I noticed some friction when I tried to ___ because ___.',
            dropdownOptions: [
              'The experience was generally fluid and fast.',
              'I noticed some friction/delay when I tried to reply to a specific message'
            ]
          }
        ]
      },
      {
        id: 'vertical_sidebar',
        name: 'Vertical side bar',
        questions: [
          {
            id: 'sidebar_q1',
            question: 'Look at a single conversation in the sidebar. Does the information shown (name, avatar, preview, time) give you enough context to know what it\'s about at a glance?',
            inputType: 'dropdown',
            placeholder: 'The sidebar gives me ___ (enough/not enough) context. I wish it also showed ___.',
            dropdownOptions: [
              'The sidebar gives me not enough context. I wish it also showed the person\'s company name right under their name.',
              'The sidebar gives me enough context.'
            ]
          }
        ]
      },
      {
        id: 'report_user_modal',
        name: 'Report user modal',
        questions: [
          {
            id: 'report_user_q1',
            question: 'Look at the list of reasons for reporting a user. Are they clear and comprehensive? Can you think of a situation that wouldn\'t be covered by these options?',
            inputType: 'dropdown',
            placeholder: 'The reasons are ___. A situation that might not be covered is ___.',
            dropdownOptions: [
              'The reasons are clear and comprehensive.',
              'The reasons are good for serious issues. A situation that might not be covered is a recruiter being unprofessional or non-responsive, which isn\'t exactly harassment.'
            ]
          },
          {
            id: 'report_user_q2',
            question: 'Beyond just reporting, is there any other action you\'d want to take immediately in that situation other than just ending the chat, such as blocking the user from contacting you again?',
            inputType: 'dropdown',
            placeholder: 'In that moment, I also wanted to be able to ___.',
            dropdownOptions: [
              'In that moment, I also wanted to be able to save or export a transcript of the conversation.',
              'In that moment, I also wanted to be able to unmatch with them, so my profile is no longer visible to their company at all.'
            ]
          }
        ]
      },
      {
        id: 'job_detail_modal',
        name: 'Job detail modal',
        questions: [
          {
            id: 'job_detail_q1',
            question: 'Did viewing the job details within the chat provide helpful context for your conversation with the recruiter? Did it change how you wanted to reply?',
            inputType: 'dropdown',
            placeholder: 'Yes/No, seeing the job details again ___ (did/didn\'t) change my reply because ___.',
            dropdownOptions: [
              'Yes, seeing the job details again did change my reply because I could reference a specific requirement in my message to them.',
              'No, seeing the job details again didn\'t change my reply because I had already reviewed the job posting carefully.'
            ]
          }
        ]
      },
      {
        id: 'end_chat',
        name: 'End chat',
        questions: [
          {
            id: 'end_chat_q1',
            question: 'In what specific situation would you actually use the \'End Chat\' feature? Is it for when a job process is over, for a group project that\'s finished, or for another reason?',
            inputType: 'dropdown',
            placeholder: 'I would probably use \'End Chat\' when ___.',
            dropdownOptions: [
              'I would probably use \'End Chat\' when the interview process with a company is officially over, either because I was rejected or I accepted another offer.',
              'I would probably use \'End Chat\' when a conversation has been silent for months and I just want to clean up my inbox.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'jobseeker_challenges',
    name: 'Jobseeker Challenges Page',
    components: [
      {
        id: 'platform_challenges',
        name: 'Platform challenges',
        questions: [
          {
            id: 'platform_challenges_q1',
            question: 'When you look at this grid of platform challenges, what\'s your immediate gut reaction? Does it feel like a valuable way to build your profile, or more like a list of generic coding problems?',
            inputType: 'dropdown',
            placeholder: 'My first impression was ___. It feels like a ___ because the challenges seem ___ (e.g., realistic, academic, too easy).',
            dropdownOptions: [
              'My first impression was that these are high-quality. It feels like a valuable way to build my profile because the challenges seem realistic',
              'My first impression was that it was another puzzle site. It feels like a list of generic coding problems because the challenges seem academic.'
            ]
          },
          {
            id: 'platform_challenges_q2',
            question: 'Developers often spend months grinding LeetCode for interviews. Do these platform challenges feel like a more direct and respectful way to demonstrate your skills for the time you invest, or do they feel like just another hoop to jump through?',
            inputType: 'dropdown',
            placeholder: 'Compared to traditional prep, these challenges feel ___ (more/less valuable) for my time because ___.',
            dropdownOptions: [
              'Compared to traditional prep, these challenges feel more valuable for my time because they test practical, on-the-job skills, not just abstract algorithms.',
              'Compared to traditional prep, these challenges feel less valuable for getting an interview because I still have to pass a LeetCode screen at most companies.'
            ]
          },
          {
            id: 'platform_challenges_q3',
            question: 'We use the number of completed challenges to unlock higher tiers (e.g., \'Cyber Architect\'). Does this feel like a meaningful progression that truly reflects your growing expertise, or does it feel more like an arbitrary grind just to level up?',
            inputType: 'dropdown',
            placeholder: 'Tying tiers to completed challenges feels ___ (motivating/meaningful/like a grind) because ___. The reward of a higher tier title feels ___.',
            dropdownOptions: [
              'Tying tiers to completed challenges feels motivating because it gives me a clear goal to work towards. The reward of a higher tier title feels like a fun achievement.',
              'Tying tiers to completed challenges feels like a grind because it encourages quantity over quality. The reward of a higher tier title feels a little silly.'
            ]
          }
        ]
      },
      {
        id: 'challenge_details_page',
        name: 'Challenge details page',
        questions: [
          {
            id: 'challenge_details_q1',
            question: 'We show the context, instructions, schema, and sample data. Is all of this information necessary before you start, or is it overwhelming? Which section is the most useful, and which is the least?',
            inputType: 'dropdown',
            placeholder: 'This amount of information felt ___. The most useful section was ___, and the least useful was ___.',
            dropdownOptions: [
              'This amount of information felt just right.',
              'This amount of information felt overwhelming. The most useful section was the Instructions, and the least useful was seeing all the sample data before starting.'
            ]
          }
        ]
      },
      {
        id: 'employer_invitations',
        name: 'Employer Invitations',
        questions: [
          {
            id: 'employer_invitations_q1',
            question: 'We explicitly state that results from these challenges are sent directly to the employer. How does this change your mindset? Does it add pressure, or does it make the challenge feel more meaningful?',
            inputType: 'dropdown',
            placeholder: 'Knowing the result goes to the employer makes me feel ___ because ___.',
            dropdownOptions: [
              'Knowing the result goes to the employer makes me feel more motivated because it\'s a real opportunity to impress them, not just for practice.',
              'Knowing the result goes to the employer makes me feel more pressure because I\'m worried that one small mistake could get me rejected.'
            ]
          },
          {
            id: 'employer_invitations_q2',
            question: 'Does receiving a direct challenge invitation make you more interested in that company? Does it feel like a genuine signal of their interest in you, or more like an automated screening tool?',
            inputType: 'dropdown',
            placeholder: 'Receiving this invitation made me feel ___ about the company. It felt like a ___ (e.g., personal request, automated test) because ___.',
            dropdownOptions: [
              'Receiving this invitation made me feel more positive about the company. It felt like a personal request because it seems like they specifically chose my profile.',
              'It felt like an automated test because I assume it\'s sent to many candidates at the same time.'
            ]
          }
        ]
      },
      {
        id: 'recording_modal',
        name: 'Recording modal',
        questions: [
          {
            id: 'recording_modal_q1',
            question: 'We break the recording into two steps: first you record your screen, then you record audio commentary while watching it back. Did this help you create a more polished explanation, or did it feel unnatural and complicated?',
            inputType: 'dropdown',
            placeholder: 'The two-step process felt ___ because ___. I would have preferred to ___.',
            dropdownOptions: [
              'The two-step process felt helpful because I could focus on solving the problem first, then give a polished explanation. I would have preferred to keep it this way.',
              'The two-step process felt unnatural because my thoughts are most relevant as I\'m coding. I would have preferred to narrate live while solving the problem.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'jobseeker_browse_jobs',
    name: 'Jobseeker Browse Jobs Page',
    components: [
      {
        id: 'discover_tab',
        name: 'Discover tab',
        questions: [
          {
            id: 'discover_q1',
            question: 'Our \'Discover\' tab uses an algorithm that analyzes your skills, challenges, and system design interview to show you the most relevant jobs first. Looking at the jobs you were shown, how well did we do? Did they feel like a personalized match for your abilities?',
            inputType: 'dropdown',
            placeholder: 'The job matches felt ___ (e.g., surprisingly accurate, somewhat relevant, mostly irrelevant). The match I was most impressed by was ___, but the match for ___ felt off.',
            dropdownOptions: [
              'The job matches felt surprisingly accurate.',
              'The job matches felt mostly irrelevant.'
            ]
          },
          {
            id: 'discover_q2',
            question: 'You have a limited number of job interactions per day. How did this limit make you feel? Did it make you more selective and thoughtful about your choices, or just frustrated?',
            inputType: 'dropdown',
            placeholder: 'The daily limit made me feel ___ because ___. It made my decision-making process more ___.',
            dropdownOptions: [
              'The daily limit made me feel frustrated because I was in a groove and wanted to keep looking at jobs.',
              'The daily limit made me feel like it was a daily task because I knew I\'d get more tomorrow.'
            ]
          },
          {
            id: 'discover_q3',
            question: 'Clicking \'Not Interested\' currently uses one of your daily interactions. Does this feel fair? We use this signal to improve your future matches, but we want to know if it feels like a penalty for giving feedback.',
            inputType: 'dropdown',
            placeholder: 'Having \'Not Interested\' count as an interaction feels ___ because ___. I would prefer if ___.',
            dropdownOptions: [
              'Having \'Not Interested\' count as an interaction feels fair because it\'s still an interaction that improves my algorithm. I would prefer if it stayed this way.',
              'Having \'Not Interested\' count as an interaction feels like a penalty because I\'m being punished for providing you with data. I would prefer if only \'Interested\' clicks counted.'
            ]
          },
          {
            id: 'discover_q4',
            question: 'We show you jobs one by one, like a stack of cards, instead of a long list. How does this experience feel? Does it help you focus on each opportunity, or do you wish you could see all your daily matches at once to compare?',
            inputType: 'dropdown',
            placeholder: 'The one-by-one format helps/hinders my process because ___. I would prefer ___.',
            dropdownOptions: [
              'The one-by-one format helps my process because it forces me to focus on one opportunity at a time. I would prefer to keep it this way.',
              'The one-by-one format hinders my process because I can\'t easily compare multiple jobs against each other. I would prefer a list view.'
            ]
          },
          {
            id: 'discover_q5',
            question: 'When making that quick \'Interested\' or \'Not Interested\' decision, what are the top 2-3 pieces of information on the card you look at?',
            inputType: 'dropdown',
            placeholder: 'I always look at ___ and ___ first.',
            dropdownOptions: [
              'I always look at the Job Title and the description first.',
              'I always look at the Company Name and its logo first.',
              'I always look at the Salary Range and Location first.'
            ]
          }
        ]
      },
      {
        id: 'saved_tab',
        name: 'Saved tab',
        questions: [
          {
            id: 'saved_q1',
            question: 'How would you want to organize saved jobs to manage your application process? Would folders, tags, or a \'matched\' filter be helpful?',
            inputType: 'dropdown',
            placeholder: 'If this list got long, I would want a way to organize it, for example by ___.',
            dropdownOptions: [
              'If this list got long, I would want a way to organize it, for example by adding my own custom tags like \'#fintech\' or \'#good-culture\'',
              'If this list got long, I would want a way to organize it, for example by creating custom folders'
            ]
          }
        ]
      },
      {
        id: 'featured_jobs_tab',
        name: 'Featured jobs tab',
        questions: [
          {
            id: 'featured_q1',
            question: 'This tab has a fixed limit of 10 interactions for everyone, regardless of subscription tier (in addition to your daily limits). Does having access to these \'free\' featured interactions make you less likely to want to upgrade for more relevant interactions in the \'Discover\' tab?',
            inputType: 'dropdown',
            placeholder: 'This feature makes me ___ likely to upgrade my tier because ___.',
            dropdownOptions: [
              'This feature makes me less likely to upgrade my tier because having 10 extra interactions, even if less relevant, might be enough for my needs.',
              'This feature makes me no less likely to upgrade my tier because I would be paying for the high-quality, relevant matches in \'Discover\', not just more interactions.'
            ]
          },
          {
            id: 'featured_q2',
            question: 'This tab shows 10 \'Featured\' jobs per day, paid for by employers. To give all companies visibility, the algorithm prioritizes jobs with less traffic, meaning they might not be the most relevant to you. Is this a trade-off you\'d be willing to make?',
            inputType: 'dropdown',
            placeholder: 'Knowing the jobs might be less relevant makes me feel ___. I would/would not check this tab because ___.',
            dropdownOptions: [
              'Knowing the jobs might be less relevant makes me feel like it\'s a waste of time. I would not check this tab because I only want to spend my time on the most relevant opportunities.',
              'Knowing the jobs might be less relevant makes me feel intrigued. I would check this tab because I might discover a \'hidden gem\'.'
            ]
          },
          {
            id: 'featured_q3',
            question: 'When you see the \'Featured\' label, what does that signal to you? Does it make you think "this is a top company," "this is an ad," or "this is a job that\'s struggling to get applicants?"',
            inputType: 'dropdown',
            placeholder: 'The \'Featured\' label makes me think ___ because ___.',
            dropdownOptions: [
              'The \'Featured\' label makes me think \'this is an advertisement\' because in most apps, \'featured\' just means someone paid for placement.',
              'The \'Featured\' label makes me think \'this is a job that\'s struggling to get applicants\' because if it were a great opportunity, it probably wouldn\'t need to be featured.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'jobseeker_search_results',
    name: 'Jobseeker Search Results Page',
    components: [
      {
        id: 'search_results',
        name: 'Search results',
        questions: [
          {
            id: 'search_results_q1',
            question: 'You can search for job titles, companies, skills, or locations. Was there anything else you tried to search for that you expected to work, such as a specific benefit or salary range?',
            inputType: 'dropdown',
            placeholder: 'I tried searching for \'___\' and was surprised it did/didn\'t work. I wish I could search by ___.',
            dropdownOptions: [
              'I tried searching for \'$150k+\' and was surprised it didn\'t work. I wish I could search by a specific salary range filter',
              'I tried searching for \'early-stage startup\' and was surprised it didn\'t work. I wish I could search by company stage or size.'
            ]
          },
          {
            id: 'search_results_q2',
            question: 'Currently, search results only show job postings. Do you feel this is too limited? Would you find it valuable to also see results for companies who match your search terms?',
            inputType: 'dropdown',
            placeholder: 'Only seeing jobs felt ___. I would/would not find it useful to also see ___ in the results because ___.',
            dropdownOptions: [
              'Only seeing jobs felt focused and efficient. I would not find it useful to also see companies in the results because I\'m only interested in roles that are actively hiring right now.',
              'Only seeing jobs felt a bit limited. I would find it useful to also see companies in the results because I\'d like to discover cool companies in my field, even if they don\'t have a perfect role open.'
            ]
          }
        ]
      },
      {
        id: 'job_detail_modal_search',
        name: 'Job detail modal',
        questions: [
          {
            id: 'job_detail_search_q1',
            question: 'In the modal, next to the company\'s name, there\'s a link to \'View Company Profile\'. Was this link easy for you to spot? Is this where you would expect to find more information about the company?',
            inputType: 'dropdown',
            placeholder: 'The company profile link was ___ to find. I was/wasn\'t expecting to find it there because ___.',
            dropdownOptions: [
              'The company profile link was easy to find. I was expecting to find it there because it\'s logically placed right next to the company name.',
              'The company profile link was hard to find. I wasn\'t expecting to find it there because honestly, I didn\'t even see it until this question pointed it out.'
            ]
          },
          {
            id: 'job_detail_search_q2',
            question: 'When you open this detailed view after a search, what\'s the first section you scroll to? What specific piece of information are you looking for that helps you confirm if this job is a good fit?',
            inputType: 'dropdown',
            placeholder: 'The first thing I looked for was the ___ section, specifically to learn about ___.',
            dropdownOptions: [
              'The first thing I looked for was the Requirements section, specifically to learn about whether my technical skills are a good match for the role.',
              'The first thing I looked for was the Salary Range section, specifically to learn about if the pay meets my expectations before I read anything else.'
            ]
          },
          {
            id: 'job_detail_search_q3',
            question: 'Thinking about the entire modal, was there any section that felt like unnecessary noise or clutter? Conversely, was there any key information about the job or company that you expected to see but was missing?',
            inputType: 'dropdown',
            placeholder: 'The ___ section felt less important to me. I was surprised that I couldn\'t find information about ___.',
            dropdownOptions: [
              'The Benefits section felt less important to me. I was surprised that I couldn\'t find information about the specific team I\'d be joining or who the hiring manager is.',
              'The Company Culture Tags section felt less important to me.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'public_company_profile',
    name: 'Public Company Profile',
    components: [
      {
        id: 'header',
        name: 'Header',
        questions: [
          {
            id: 'header_q1',
            question: 'Of the details in the header (location, industry, company size, founded year), which one is most important to you in forming an initial opinion? Is there any key info missing?',
            inputType: 'dropdown',
            placeholder: 'The detail I look at first is ___, because ___. I was surprised I couldn\'t find ___ in the header.',
            dropdownOptions: [
              'The detail I look at first is Company Size, because it gives me a quick idea of the company\'s stage and stability.',
              'The detail I look at first is Industry, because I want to know if I\'m interested in their product space.'
            ]
          },
          {
            id: 'header_q2',
            question: 'We provide links to the company\'s website, LinkedIn, and GitHub. How likely are you to click on these? Which one is the most valuable for your research?',
            inputType: 'dropdown',
            placeholder: 'I would most likely click the ___ link to find out ___. The other links are ___ valuable to me.',
            dropdownOptions: [
              'I would most likely click the GitHub link to find out if they have open-source projects and see their code quality.',
              'I would most likely click the Website link to find out exactly what their product does.'
            ]
          }
        ]
      },
      {
        id: 'company_culture_display',
        name: 'Company culture display',
        questions: [
          {
            id: 'culture_q1',
            question: 'Of the different sections here—Work Environment, Benefits, Tech Stack, Growth—which one do you care about the most when evaluating a company?',
            inputType: 'dropdown',
            placeholder: 'The section I care most about is ___ because ___.',
            dropdownOptions: [
              'The section I care most about is Growth because I want to see a clear path for advancement.',
              'The section I care most about is Tech Stack because I want to make sure I\'ll be working with modern tools.',
              'The section I care most about is Benefits because total compensation and work-life balance are my top priorities.'
            ]
          },
          {
            id: 'culture_q2',
            question: 'Some information is hidden in collapsible sections. Did you feel motivated to click and expand them to learn more, or did you just skim the top-level cards?',
            inputType: 'dropdown',
            placeholder: 'I did/did not expand the sections. I chose to expand the \'___\' section because I wanted to know about ___.',
            dropdownOptions: [
              'I did not expand the sections. I chose to expand none of them because the high-level information on the cards was enough for me.',
              'I did expand the sections.'
            ]
          },
          {
            id: 'culture_q3',
            question: 'What\'s one piece of information in this section that would be a \'green flag\' for you (making you very interested)? What\'s one piece of information that would be a \'red flag\'?',
            inputType: 'dropdown',
            placeholder: 'A green flag for me would be seeing ___. A red flag would be ___.',
            dropdownOptions: [
              'A green flag for me would be seeing a structured promotion process with an IC track. A red flag would be \'Ad-hoc\' promotions.',
              'A green flag for me would be seeing a detailed \'Company Culture Description\'. A red flag would be generic values like \'Work Hard, Play Hard\'.'
            ]
          }
        ]
      },
      {
        id: 'job_openings_display',
        name: 'Job openings display',
        questions: [
          {
            id: 'job_openings_q1',
            question: 'This section shows a preview of the company\'s open jobs. When you first glance at this list, what are you trying to figure out? (e.g., \'Do they hire for my role?\', \'Are the roles remote?\').',
            inputType: 'dropdown',
            placeholder: 'The first thing I look for in this list is ___ to see if ___.',
            dropdownOptions: [
              'The first thing I look for in this list is job titles to see if they are hiring for my specific role.',
              'The first thing I look for in this list is the location or \'Remote\' tag to see if the logistics would work for me'
            ]
          }
        ]
      },
      {
        id: 'see_all_jobs_modal',
        name: 'See all jobs modal',
        questions: [
          {
            id: 'see_all_jobs_q1',
            question: 'When you click \'See All Jobs,\' we open this list in a modal window instead of taking you to a new page. Does this feel like a smooth, focused experience, or would you prefer a dedicated page?',
            inputType: 'dropdown',
            placeholder: 'The modal experience felt ___ because ___. I would prefer a ___.',
            dropdownOptions: [
              'The modal experience felt smooth and focused because it kept me on the company profile without losing my place. I would prefer a modal like this.',
              'The modal experience felt a bit cramped because I couldn\'t bookmark the page or open jobs in new tabs easily. I would prefer a dedicated page.'
            ]
          },
          {
            id: 'see_all_jobs_q2',
            question: 'Is there any information you wished was visible on this list view that would have saved you a click into the full job details?',
            inputType: 'dropdown',
            placeholder: 'I wish this list also showed ___ for each job so I didn\'t have to click into every one.',
            dropdownOptions: [
              'I wish this list also showed the salary range for each job so I didn\'t have to click into every one.',
              'I wish this list also showed a few of the main required skills for each job so I didn\'t have to click into every one.'
            ]
          },
          {
            id: 'see_all_jobs_q3',
            question: 'This view has its own search and filter controls. Did you find yourself using these tools to narrow down the list? Were they easy to use within the modal?',
            inputType: 'dropdown',
            placeholder: 'I did/did not use the search and filters inside the modal because ___. They were ___ to use.',
            dropdownOptions: [
              'I did use the search and filters inside the modal because the list was long and I was looking for a specific keyword. They were easy to use',
              'I did not use the search and filters inside the modal because there weren\'t enough jobs to need them; it was easier to just scroll.'
            ]
          }
        ]
      },
      {
        id: 'interview_process_display',
        name: 'Interview process display',
        questions: [
          {
            id: 'interview_process_q1',
            question: 'What is the single most useful piece of information in this section? Is it the number of stages, the typical timeline, the types of technical assessments, or something else?',
            inputType: 'dropdown',
            placeholder: 'The most useful piece of information was the ___ because it told me ___.',
            dropdownOptions: [
              'The most useful piece of information was the typical timeline because it told me how much of a time commitment I\'d be looking at',
              'The most useful piece of information was the types of technical assessments because it told me if I needed to study for a live coding session or a take-home project.'
            ]
          },
          {
            id: 'interview_process_q2',
            question: 'Does seeing this information make the prospect of interviewing with this company feel less intimidating? What part of this display is most effective at reducing interview-related anxiety?',
            inputType: 'dropdown',
            placeholder: 'Yes/No, it made interviewing here feel ___ intimidating. The part about ___ was the most reassuring.',
            dropdownOptions: [
              'Yes, it made interviewing here feel less intimidating. The part about knowing the exact number of stages upfront was the most reassuring.',
              'No, it made interviewing here feel just as intimidating.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'subscription_tiers',
    name: 'Subscription Tiers Page',
    components: [
      {
        id: 'subscription_tiers_page',
        name: 'Subscription tiers page',
        questions: [
          {
            id: 'subscription_q1',
            question: 'Looking at the prices—especially \'$50/month\' for Pro and \'$250/year\' for Founding Engineer—what\'s your gut reaction? Does this pricing feel fair, expensive, or cheap for the value being offered to advance your career?',
            inputType: 'dropdown',
            placeholder: 'My gut reaction to the pricing was ___. It feels ___ for what\'s being offered because ___.',
            dropdownOptions: [
              'My gut reaction to the pricing was that it\'s for serious professionals. It feels fair for what\'s being offered because it\'s an investment in my career, not just a casual subscription.',
              'My gut reaction to the pricing was sticker shock. It feels expensive for what\'s being offered because I\'m used to paying much less for monthly software subscriptions.'
            ]
          },
          {
            id: 'subscription_q2',
            question: 'What would be the most likely real-world trigger for you to upgrade? Would it be hitting your daily interaction limit, needing more interview attempts, or wanting the ability to message an employer first?',
            inputType: 'dropdown',
            placeholder: 'The thing that would most likely make me upgrade is ___ because ___.',
            dropdownOptions: [
              'The thing that would most likely make me upgrade is needing more System Design Interview attempts because it\'s valuable practice and one per month isn\'t enough.',
              'The thing that would most likely make me upgrade is constantly hitting the daily job interaction limit because it\'s frustrating to be blocked when I\'m actively searching.'
            ]
          },
          {
            id: 'subscription_q3',
            question: 'Was it easy to understand the differences between the three plans at a glance? Was there any feature listed that you didn\'t understand or that seemed unimportant to you?',
            inputType: 'dropdown',
            placeholder: 'The plans were ___ to compare. I didn\'t really understand what ___ meant, and the feature \'\' didn\'t seem very valuable.',
            dropdownOptions: [
              'The plans were very easy to compare. I didn\'t have any trouble understanding the features, and they all seemed to have clear value.',
              'The plans were easy to compare. I didn\'t really understand what the different number of interview attempts meant in practice, and the feature \'increasing the number of featured projects\' didn\'t seem very valuable'
            ]
          }
        ]
      },
      {
        id: 'stripe_checkout',
        name: 'Stripe checkout',
        questions: [
          {
            id: 'stripe_q1',
            question: 'For this alpha test, we provided instructions with a sample credit card number. Were these instructions clear? Did you have any trouble using the test card to complete the checkout simulation?',
            inputType: 'dropdown',
            placeholder: 'The instructions were ___. I did/did not have trouble using the test card because ___.',
            dropdownOptions: [
              'The instructions were very clear. I did not have trouble using the test card because all the fields like CVC and expiry date were provided.',
              'The instructions were a little confusing.'
            ]
          },
          {
            id: 'stripe_q2',
            question: 'After completing the checkout, you were redirected back to our site. Was it immediately clear that your upgrade was successful? Did your plan status and features update as you expected?',
            inputType: 'dropdown',
            placeholder: 'After paying, it was/was not clear that it worked. I checked ___ to confirm my new status.',
            dropdownOptions: [
              'After paying, it was clear that it worked. I checked the Subscription tab in my settings to confirm my new status.',
              'After paying, it was not clear that it worked. I didn\'t know where to check and just assumed it was active.'
            ]
          },
          {
            id: 'stripe_q3',
            question: 'If you tried to downgrade your plan, we showed a confirmation modal explaining that your paid features would last until the end of your billing cycle. Was this process and explanation clear?',
            inputType: 'dropdown',
            placeholder: 'The downgrade process was ___. I was/was not surprised that my plan would continue until the end of the cycle because ___.',
            dropdownOptions: [
              'The downgrade process was clear and straightforward. I was not surprised that my plan would continue until the end of the cycle.',
              'The downgrade process was a bit confusing. I was surprised that my plan would continue until the end of the cycle because I expected it to cancel immediately.'
            ]
          },
          {
            id: 'stripe_q4',
            question: 'Thinking about the entire process of subscribing and potentially downgrading, how easy did it feel to manage your subscription? Do you feel like you are in control of your billing?',
            inputType: 'dropdown',
            placeholder: 'Overall, managing my subscription felt ___. I feel ___ in control of my billing because ___',
            dropdownOptions: [
              'Overall, managing my subscription felt very easy and transparent. I feel completely in control of my billing because I can upgrade or cancel myself with just a few clicks.',
              'Overall, managing my subscription felt a little nerve-wracking. I felt not fully in control of my billing'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'analytics_page',
    name: 'Analytics Page',
    components: [
      {
        id: 'stats_cards',
        name: 'Total views/views this month/unique companies cards',
        questions: [
          {
            id: 'stats_cards_q1',
            question: 'If you\'re on the free plan, you see that this data is being tracked but is locked. Does seeing these locked numbers make you curious and motivate you to upgrade, or does it mainly feel frustrating?',
            inputType: 'dropdown',
            placeholder: 'Seeing the locked numbers feels ___ because ___. It does/does not make me want to upgrade.',
            dropdownOptions: [
              'Seeing the locked numbers feels motivating because it shows valuable data exists and I\'m curious to see it. It does make me want to upgrade.',
              'Seeing the locked numbers feels frustrating because it feels like you\'re holding basic information hostage. It does not make me want to upgrade.'
            ]
          },
          {
            id: 'stats_cards_q2',
            question: 'Are there any other high-level stats you wish you could see here at a glance? For example, the number of times you\'ve appeared in search results, or a week-over-week growth percentage?',
            inputType: 'dropdown',
            placeholder: 'I wish this section also had a card for ___ because ___.',
            dropdownOptions: [
              'I wish this section also had a card for how many times my profile appeared in search results, because it would show me if my keywords and skills are effective for discoverability.',
              'I wish this section also had a card for how my views compare to similar users, because I want to know if my numbers are average or above average.'
            ]
          }
        ]
      },
      {
        id: 'profile_views_trend',
        name: 'Profile views trend chart',
        questions: [
          {
            id: 'trend_q1',
            question: 'This chart is a premium feature. How much more valuable is seeing this trend data compared to just the total view count? Is this a feature that would convince you to upgrade?',
            inputType: 'dropdown',
            placeholder: 'Seeing the trend is ___ more valuable than a single number. This feature does/does not make me want to upgrade because ___.',
            dropdownOptions: [
              'Seeing the trend is significantly more valuable than a single number. This feature does make me want to upgrade because it helps me see which of my activities actually lead to more views.',
              'Seeing the trend is not much more valuable than a single number. This feature does not make me want to upgrade because I only really care about the total view count, not the daily changes.'
            ]
          }
        ]
      },
      {
        id: 'top_companies',
        name: 'Top companies',
        questions: [
          {
            id: 'companies_q1',
            question: 'When you see a company on this list that you\'re interested in, what is your immediate next step? Do you research them, try to find a connection, or something else?',
            inputType: 'dropdown',
            placeholder: 'When I saw \'___\' on the list, my next step was to ___.',
            dropdownOptions: [
              'When I saw \'xyz corp.\' on the list, my next step was to research their product and recent news to see if it\'s a company I\'m actually interested in.',
              'When I saw \'xyz corp.\' on the list, my next step was to immediately check their company profile on this platform to see their open jobs.'
            ]
          },
          {
            id: 'companies_q2',
            question: 'The list shows the company name and how many recruiters viewed your profile. What other information would be useful to see here? For example, the date of their last visit?',
            inputType: 'dropdown',
            placeholder: 'I wish this list also showed ___ for each company, because it would help me ___.',
            dropdownOptions: [
              'I wish this list also showed the date of their last visit for each company, because it would help me know whether their interest is recent.',
              'I wish this list also showed which specific recruiter viewed my profile for each company, because it would help me reach out to the right person directly.'
            ]
          }
        ]
      },
      {
        id: 'challenge_performance',
        name: 'Challenge performance',
        questions: [
          {
            id: 'challenge_perf_q1',
            question: 'Is a \'view\' on a challenge a strong enough signal of interest for you? Or would you want to know more, like how long they watched the video or if they came from a specific company?',
            inputType: 'dropdown',
            placeholder: 'A view count is ___. I would find it much more valuable if I could also see ___.',
            dropdownOptions: [
              'A view count is a good starting point, but not enough. I would find it much more valuable if I could also see how long they watched the video, to know if they were actually engaged.',
              'A view count is okay, but lacks context. I would find it much more valuable if I could also see which specific companies are viewing my challenge solutions.'
            ]
          },
          {
            id: 'challenge_perf_q2',
            question: 'Does seeing the performance of your challenges motivate you to complete more challenges, especially in categories that seem to get a lot of attention?',
            inputType: 'dropdown',
            placeholder: 'Yes/No, this data encourages me to take more challenges in ___ because ___.',
            dropdownOptions: [
              'Yes, this data encourages me to take more challenges in categories with fewer views because I want to demonstrate a broader skillset.',
              'No, this data doesn\'t encourage me to take more challenges in any specific area because I choose challenges based on what I want to learn, not on popularity.'
            ]
          },
          {
            id: 'challenge_perf_q3',
            question: 'This is a premium feature. How valuable is this granular, challenge-level performance data in your decision to potentially upgrade?',
            inputType: 'dropdown',
            placeholder: 'Knowing which of my specific works gets viewed is ___ important to me. It does/doesn\'t make me want to upgrade because ___.',
            dropdownOptions: [
              'Knowing which of my specific works gets viewed is very important to me. It does make me want to upgrade because it provides direct feedback on which skills are attracting employers.',
              'Knowing which of my specific works gets viewed is not very important to me. It doesn\'t make me want to upgrade because I\'m more focused on the total profile views, not the details.'
            ]
          }
        ]
      },
      {
        id: 'project_performance',
        name: 'Project performance',
        questions: [
          {
            id: 'project_perf_q1',
            question: 'If you saw that one of your featured projects had significantly more views than another, would you consider swapping out the lower-performing project for a different one from your portfolio?',
            inputType: 'dropdown',
            placeholder: 'Yes/No, seeing these numbers would/would not make me change which projects I feature because ___.',
            dropdownOptions: [
              'Yes, seeing these numbers would make me change which projects I feature because I want to optimize my profile to show what recruiters find most interesting.',
              'No, seeing these numbers would not make me change which projects I feature because I choose projects that represent the skills I want to use, not just what\'s popular.'
            ]
          },
          {
            id: 'project_perf_q2',
            question: 'Besides the number of views, what other metric would be most helpful for understanding your project\'s performance? Clicks to the live URL? Clicks to the GitHub repo?',
            inputType: 'dropdown',
            placeholder: 'In addition to views, the most useful metric would be ___ because ___.',
            dropdownOptions: [
              'The most useful metric would be the average time someone spent looking at the project, because it would tell me if they were actually reading the description or just skimming.',
              'The most useful metric would be which companies are viewing which project, because it would help me tailor my follow-ups.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'settings_page',
    name: 'Settings Page',
    components: [
      {
        id: 'account_settings',
        name: 'Account settings',
        questions: [
          {
            id: 'account_q1',
            question: 'To change your password, we ask you to enter the new password twice but don\'t require your old one. How does this feel from a security perspective? Is it what you expected?',
            inputType: 'dropdown',
            placeholder: 'This password change flow felt ___ from a security standpoint. I was/wasn\'t expecting to provide my old password.',
            dropdownOptions: [
              'This password change flow felt a little insecure from a security standpoint. I was expecting to provide my old password to confirm my identity before making a change.',
              'This password change flow felt fine from a security standpoint. I wasn\'t expecting to provide my old password, since I\'m already logged in. It\'s similar to a password reset.'
            ]
          },
          {
            id: 'account_q2',
            question: 'Beyond email and password, are there any other account settings you were looking for here? For example, connecting social accounts, or changing your username/URL?',
            inputType: 'dropdown',
            placeholder: 'I was surprised I couldn\'t find a setting for ___.',
            dropdownOptions: [
              'I was surprised I couldn\'t find a setting for connecting my other accounts, like GitHub or LinkedIn, to my profile.',
              'I was surprised I couldn\'t find a setting for exporting all of my data, like my projects and challenge solutions, to a single file.'
            ]
          }
        ]
      },
      {
        id: 'notifications',
        name: 'Notifications',
        questions: [
          {
            id: 'notifications_q1',
            question: 'By default, all notifications are turned on. Is this the behavior you would expect, or would you prefer to opt-in to receiving emails?',
            inputType: 'dropdown',
            placeholder: 'I would prefer notifications to be on/off by default because ___.',
            dropdownOptions: [
              'I would prefer notifications to be on by default because I want to know about important activity and might forget to enable them myself.',
              'I would prefer notifications to be off by default because my inbox is already crowded, and I want to consciously choose which emails I receive.'
            ]
          },
          {
            id: 'notifications_q2',
            question: 'Are there any other events on the platform for which you\'d want to receive an email notification? For example, when someone views your profile?',
            inputType: 'dropdown',
            placeholder: 'I would find it very useful to get an email notification when ___.',
            dropdownOptions: [
              'I would find it very useful to get an email notification when a company views my profile, so I can research them and prepare for potential outreach.',
              'I would find it very useful to get an email notification when an employer views my challenge submission or moves me to the next stage in their process.'
            ]
          }
        ]
      },
      {
        id: 'privacy',
        name: 'Privacy',
        questions: [
          {
            id: 'privacy_q1',
            question: 'We offer three visibility modes: \'Private\', \'Showcase\' (link only), and \'Open\'. Is the difference between these three options immediately clear? In what situation would you use each one?',
            inputType: 'dropdown',
            placeholder: 'The difference between the modes was . I would use \'\' mode when ___.',
            dropdownOptions: [
              'The difference between the modes was very clear. I would use \'Showcase\' mode when I\'m not actively looking but want to send a direct link to someone in my network.',
              'The difference between the modes was a little confusing. I would use \'Private\' mode when I\'m not job seeking, as I don\'t really see the point of the \'Showcase\' option.'
            ]
          },
          {
            id: 'privacy_q2',
            question: 'Your profile visibility defaults to \'Open\' to be discoverable by employers. How do you feel about this? Would you prefer it to start as \'Private\'?',
            inputType: 'dropdown',
            placeholder: 'I feel ___ about my profile being \'Open\' by default because ___.',
            dropdownOptions: [
              'I feel good about my profile being \'Open\' by default because the main reason I\'m here is to be discovered by employers.',
              'I feel uncomfortable about my profile being \'Open\' by default because I want to control the exact moment it becomes visible, not have it happen automatically.'
            ]
          },
          {
            id: 'privacy_q3',
            question: 'Are there any other privacy controls you wish you had? For example, the ability to block specific companies from viewing your profile?',
            inputType: 'dropdown',
            placeholder: 'I wish I had the ability to ___.',
            dropdownOptions: [
              'I wish I had the ability to block specific companies, like my current employer, from finding or viewing my profile.',
              'I wish I had the ability to do nothing else; the current options for \'Private\', \'Showcase\', and \'Open\' cover all my needs.'
            ]
          }
        ]
      },
      {
        id: 'subscription_tab',
        name: 'Subscription tab',
        questions: [
          {
            id: 'subscription_tab_q1',
            question: 'We show a history of your subscription events (subscribed, upgraded, etc.). Is this information useful to you? Is there any other detail you\'d want to see in your billing history?',
            inputType: 'dropdown',
            placeholder: 'The history is/is not useful. I wish it also included ___.',
            dropdownOptions: [
              'The history is useful for seeing a quick log of plan changes. I wish it also included the amount I was charged for each transaction.',
              'The history is not very useful on its own. I wish it also included a link to download a PDF invoice for my records.'
            ]
          },
          {
            id: 'subscription_tab_q2',
            question: 'Overall, does this interface give you confidence that you can manage your subscription easily and won\'t be charged unexpectedly? What could improve your trust in our billing system?',
            inputType: 'dropdown',
            placeholder: 'This page gives me ___ confidence in managing my billing. My trust would increase if ___.',
            dropdownOptions: [
              'This page gives me high confidence in managing my billing. My trust would increase if nothing; the ability to easily downgrade or change plans myself is all I need.',
              'This page gives me low confidence in managing my billing. My trust would increase if I could manage my payment method directly on this page.'
            ]
          }
        ]
      },
      {
        id: 'report_bug_tab',
        name: 'Report a bug tab',
        questions: [
          {
            id: 'bug_q1',
            question: 'After you submit a bug report, what do you expect to happen next? Do you expect a personal reply, a status update, or just to see the bug fixed eventually?',
            inputType: 'dropdown',
            placeholder: 'After submitting, my expectation is that ___.',
            dropdownOptions: [
              'My expectation is that I\'d get an automated email confirming you received it, and maybe a personal reply if more details are needed',
              'My expectation is that I probably won\'t hear anything back, but I hope to see the bug fixed in a future update.'
            ]
          },
          {
            id: 'bug_q2',
            question: 'The system prevents you from submitting reports too frequently. Did you encounter this? Does a 5-minute cooldown feel like a reasonable way to prevent spam?',
            inputType: 'dropdown',
            placeholder: 'I did/did not encounter the cooldown. It seems ___.',
            dropdownOptions: [
              'I did not encounter the cooldown. It seems like a reasonable way to prevent spam without being too restrictive.',
              'I did encounter the cooldown. It seems a bit too long; I found a second bug right after the first and had to wait to report it.'
            ]
          }
        ]
      },
      {
        id: 'danger_zone',
        name: 'Danger zone',
        questions: [
          {
            id: 'danger_q1',
            question: 'Before deleting your account, is there any other action you might want to take? For example, would you want to download a copy of all your data first?',
            inputType: 'dropdown',
            placeholder: 'Before deleting, I would want to ___.',
            dropdownOptions: [
              'Before deleting, I would want to download a backup of my projects and challenge solutions.',
              'Before deleting, I would want to do nothing else; the confirmation is enough, I just want the account gone.'
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'terms_conditions',
    name: 'Terms and Conditions Page',
    components: [
      {
        id: 'terms_and_conditions',
        name: 'Terms and conditions',
        questions: [
          {
            id: 'terms_q1',
            question: 'Most people don\'t read these pages. We\'ve tried to make ours clear and easy to navigate with the sidebar on the left. What\'s your immediate impression? Does it look intimidating, or more accessible than usual?',
            inputType: 'dropdown',
            placeholder: 'My first impression was that this page looks ___. Compared to other terms pages, it feels ___ (e.g., more organized, just as dense) because ___.',
            dropdownOptions: [
              'Compared to other terms pages, it feels more organized because the sidebar lets me jump to sections easily.',
              'Compared to other terms pages, it feels just as dense because it\'s still a wall of text I won\'t read.'
            ]
          },
          {
         id: 'terms_q2',
            question: 'A major concern for developers is who owns their work. Without reading the entire document, what is your assumption about the ownership of the code and projects you upload to our platform? Does using Techfolio make you feel secure about your intellectual property?',
            inputType: 'dropdown',
            placeholder: 'My assumption is that I/the platform own(s) my work. I feel ___ secure about my IP here because ___.',
            dropdownOptions: [
              'My assumption is that I own my work. I feel very secure about my IP here because it\'s a portfolio site, so ownership is implied.',
              'My assumption is that I own my work, but you get a license. I feel reasonably secure about my IP here because you need a license to show my work to employers.'
            ]
          },
          {
            id: 'terms_q3',
            question: 'After glancing at this page, do you feel more or less trust in our platform to handle your data and career opportunities fairly? What\'s one thing on this page (even just a section title) that stands out as either particularly reassuring or concerning?',
            inputType: 'dropdown',
            placeholder: 'This page makes me feel ___ trust in the platform. The section about \'___\' stood out to me as reassuring/concerning because ___.',
            dropdownOptions: [
              'This page makes me feel more trust in the platform. The section about \'Submission Guidelines\' stood out to me as reassuring because it shows you care about originality.',
              'This page makes me feel less trust in the platform. The section about \'Limitation of Liability\' stood out to me as concerning because it feels like it only protects the company'
            ]
          }
        ]
      }
    ]
  }
];


export const getPageNames = (): { id: string; name: string }[] => {
  return betaTestData.map(page => ({ id: page.id, name: page.name }));
};


export const getComponentsForPages = (pageIds: string[]): { pageId: string; pageName: string; components: { id: string; name: string }[] }[] => {
  return pageIds.map(pageId => {
    const page = betaTestData.find(p => p.id === pageId);
    if (!page) return { pageId, pageName: '', components: [] };
    
    return {
      pageId: page.id,
      pageName: page.name,
      components: page.components.map(comp => ({ id: comp.id, name: comp.name }))
    };
  });
};


export const getQuestionsForComponents = (componentIds: string[]): BetaTestComponent[] => {
  const components: BetaTestComponent[] = [];
  
  betaTestData.forEach(page => {
    page.components.forEach(component => {
      if (componentIds.includes(component.id)) {
        components.push(component);
      }
    });
  });
  
  return components;
};


export interface BetaTestResponse {
  userId: string;
  userEmail: string;
  userName: string;
  submittedAt: string;
  selectedPages: string[];
  selectedComponents: string[];
  responses: {
    componentId: string;
    componentName: string;
    questionId: string;
    question: string;
    answer: string;
  }[];
}